import { Component, Input, Output, EventEmitter } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, lastValueFrom, takeUntil } from 'rxjs';

import { FaqService } from '@core/services/faq/faq.service';
import { OrderApiService } from '@app/core/http/order-api.service';
import { StorageService } from '@core/services/util/storage.service';
import { ChatbotService } from '@app/core/services/chatbot/chatbot.service';
import { SessionStorageService } from '@app/core/services/util/session-storage.service';
import { OrdersUtilService } from '@app/core/services/orders-util/orders-util.service';

import { BaseComponent } from "@app/core/components/base/base.component";
import { MatExpansionModule } from '@angular/material/expansion';
import { ToastrService } from '@app/core/services/notification/toastr.service';

@Component({
    selector: 'app-faq',
    templateUrl: './faq.component.html',
    styleUrls: ['./faq.component.scss'],
    standalone: true,
    imports: [MatExpansionModule]
})
export class FaqComponent extends BaseComponent {
    faqs: any;
    is_logged_in = false;
    isCancellationFlow = false;
    status = '';
    indexFaq = 0;
    tracking_id = '';
    state: any;
    flow: 'indexFlow' | 'orderFlow';
    @Input() orderData;
    @Output() refresh = new EventEmitter<any>();
    destroySubject$: Subject<void> = new Subject();

    constructor(
        private faqService: FaqService,
        public router: Router,
        private toastrService: ToastrService,
        private sessionStorageService: SessionStorageService,
        private storageService: StorageService,
        private orderApiService: OrderApiService,
        private ordersUtilService: OrdersUtilService,
        private chatbotService: ChatbotService
    ) {
        super();
        this.state = this.router.getCurrentNavigation()?.extras.state || null;
    }

    ngOnInitBrowser() {
        this.chatConfig();
        this.is_logged_in = this.storageService.get('session-id') && this.storageService.get('phone_no');
        this.setData(this.state || this.orderData);
        this.fetchFaqs(this.status);
    }

    setData(data) {
        if (data?.status) {
            this.status = data.status;
            this.tracking_id = data.tracking_id;
            this.flow = 'orderFlow';
        } else if (data?.selectedFaq) {
            this.faqs = data?.selectedFaq.faqs;
            this.flow = 'indexFlow';
        }
    }

    async fetchFaqs(status) {
        if (!this.faqs) {
            const faqs$ = await this.faqService.fetchDetails(status);
            lastValueFrom(faqs$)
                .then((resp: any) => {
                    let faqData;
                    if (this.flow === 'orderFlow') {
                        faqData = resp.faq_types;
                    } else {
                        const faqId = this.router.url.split('/')[2];
                        faqData = resp.faq_types.filter(data => data.id == faqId);
                    }
                    if (faqData?.length) {
                        this.faqs = faqData[0].faqs;
                    } else {
                        this.router.navigateByUrl('/help');
                    }
                })
                .catch(err => console.log(err));
        }
    }

    async openChat(faq) {
        // ensure sdk is loaded
        this.chatbotService.chatConfig().then(() => {
            this.chatbotService.addMessageListener();

            // Retrieve existing FAQ data from sessionStorage
            const faqData = this.sessionStorageService.get('faq_details') || {};

            // Add the new FAQ entry
            faqData[faq.id] = {
                context: faq.context,
                question: faq.question
            };

            // Update the FAQ data in sessionStorage
            this.sessionStorageService.set('faq_details', faqData);

            let ymAuthenticationToken = null;

            if (this.is_logged_in) {
                ymAuthenticationToken = `$${this.storageService.get('customer_id')}$${this.tracking_id}$${faq.id}$`;
            } else {
                ymAuthenticationToken = this.storageService.get('ymAuthToken');
                if (!ymAuthenticationToken) {
                    const expiryDate = new Date();
                    expiryDate.setDate(expiryDate.getDate() + 1);
                    ymAuthenticationToken = `$${this.createUUID()}$${faq.id}$`;
                    this.storageService.set('ymAuthToken', ymAuthenticationToken, expiryDate);
                }
            }

            const config = {
                ymAuthenticationToken: ymAuthenticationToken,
                payload: {
                    query: faq.question,
                    user_name: this.storageService.get('name'),
                    email_id: this.storageService.get('email'),
                    phone_no: this.storageService.get('phone_no'),
                    tracking_id: this.tracking_id,
                    auth_token: this.storageService.get('session-id'),
                    journey: faq.context,
                    faq_id: faq.id
                }
            };

            if (faq.context === 'cancellation-flow') {
                this.isCancellationFlow = true;
            }

            if (window && window['YellowMessengerPlugin']) {
                window['YellowMessengerPlugin'].init(config);
                window['YellowMessengerPlugin'].openBot();
            }
        }).catch(() => {
            this.toastrService.openSnackBar('Something went wrong. Please try again!', 'OKAY');
        });
    }

    createUUID() {
        let dt = new Date().getTime();
        const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            const r = (dt + Math.random() * 16) % 16 | 0;
            dt = Math.floor(dt / 16);
            return (c == 'x' ? r : (r & 0x3) | 0x8).toString(16);
        });
        return uuid;
    }

    chatConfig() {
        this.chatbotService.chatConfig();

        this.chatbotService.isChatBotClosed.pipe(takeUntil(this.destroySubject$)).subscribe(() => {
            this.isCancellationFlow = false;
            if (this.status !== 'cancelled') {
                this.fetchOrders();
            }
        });
    }

    fetchOrders() {
        this.orderApiService
            .getCustomerOrders()
            .toPromise()
            .then((data: any) => {
                if (data?.customer_orders?.length) {
                    const customerOrders = data.customer_orders;
                    this.ordersUtilService.customerOrdersState.next(data.customer_orders);

                    const cancelledOrder = customerOrders.filter(
                        order => order.tracking_id === this.tracking_id && order.order_status.status === 'cancelled'
                    );

                    if (cancelledOrder.length) {
                        this.fetchFaqs('cancelled');
                        this.refresh.emit(true);
                    }
                }
            }).catch((err) => {
                console.log('faq Screen:getCustomerOrders', err);
            })
    }

    closeChat() {
        if (window && window['YellowMessengerPlugin']) {
            window['YellowMessengerPlugin'].closeBot();
        }
    }

    ngOnDestroyBrowser() {
        this.closeChat();
        this.destroySubject$.next();
        this.destroySubject$.complete();
    }
}
